/**
 * Utility functions for handling authentication redirects
 * Preserves destination URL with all query and path parameters
 * Uses Redux for storing redirect information
 */

import store from '../../store';
import { setRedirectInfo as setRedirectInfoAction, clearRedirectInfo as clearRedirectInfoAction } from '../../GlobalModules/Login/Actions/LoginAction';

/**
 * Save redirect information for authentication flow
 * 
 * @param {Object} redirectInfo - Information about where to redirect after authentication
 * @param {string} redirectInfo.path - Path to redirect to (e.g., "/en/tour-details/some-tour")
 * @param {Object} redirectInfo.params - Query parameters to include
 * @param {string} redirectInfo.slug - Optional slug parameter (for detail pages)
 */
export const saveRedirectInfo = (redirectInfo) => {
  try {
    store?.dispatch(setRedirectInfoAction(redirectInfo));
  } catch (error) {
    console.error('Error saving redirect info to Redux:', error);
  }
};

/**
 * Get saved redirect information
 * 
 * @returns {Object|null} The saved redirect information or null if not found
 */
export const getRedirectInfo = async() => {
  try {
    // Get redirect info from Redux store
    const state =await store?.getState();
    return state.LoginReducer.redirectInfo;
  } catch (error) {
    console.error('Error getting redirect info from Redux:', error);
    return null;
  }
};

/**
 * Clear redirect information
 */
export const clearRedirectInfo = () => {
  store?.dispatch(clearRedirectInfoAction());
};

/**
 * Create a redirect URL based on saved information
 * 
 * @param {string} language - Language code (e.g., "en")
 * @returns {string} The URL to redirect to, or default home page if no info found
 */
export const createRedirectUrl = async (language) => {
  const redirectInfo =await getRedirectInfo();
  
  if (!redirectInfo) {
    return `/${language}/home`;
  }

  // Start with the base path
  let url = redirectInfo.path;
  
  // Add slug if it exists and the path ends with a page type that uses slugs
  if (redirectInfo.slug && 
      (url.endsWith('property-details') || 
       url.endsWith('tour-details'))) {
    url = `${url}/${redirectInfo.slug}`;
  }
  
  // Add query parameters if they exist
  if (redirectInfo.params && Object.keys(redirectInfo.params).length > 0) {
    const searchParams = new URLSearchParams();
    
    Object.entries(redirectInfo.params).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        // Handle array values by joining with commas
        if (value.length > 0) {
          searchParams.append(key, value.join(','));
        }
      } else if (value !== undefined && value !== null && value !== '') {
        // Only add defined values
        searchParams.append(key, value.toString());
      }
    });
    
    const queryString = searchParams.toString();
    if (queryString) {
      url = `${url}?${queryString}`;
    }
  }
  
  return url;
};

/**
 * Save current location as redirect target and redirect to auth page
 * 
 * @param {Object} history - React Router history object
 * @param {string} currentPath - Current path
 * @param {Object} searchParams - Current search parameters
 * @param {string} authType - Authentication page type ('login', 'register', 'forgot-password')
 * @param {string} language - Language code
 */
export const redirectToAuth = (history, currentPath, searchParams, authType, language) => {
  const params = {};
  // console.log("redirectToAuth",searchParams);
  // Convert URLSearchParams to plain object
  for (const [key, value] of searchParams.entries()) {
    // Handle potential array values (comma-separated)
    if (value.includes(',')) {
      params[key] = value.split(',');
    } else {
      params[key] = value;
    }
  }
  
  // Parse out slug from path if it exists (for detail pages)
  let slug = null;
  let path = currentPath;
  
  // Extract slug from paths like /en/property-details/hotel-abc or /en/tour-details/tour-xyz
  if (currentPath.includes('/property-details/') || currentPath.includes('/tour-details/')) {
    const pathParts = currentPath.split('/');
    slug = pathParts.pop();  // Extract the slug
    path = pathParts.join('/');  // Reconstruct the path without slug
  }
  
  // Save redirect info using the existing utility function
  saveRedirectInfo({ path, params, slug });
  
  // Redirect to the appropriate auth page with language prefix
  const lang = language || 'en';
  const authPath = `/${lang}/${authType}`;

  // Execute the redirect
  history.push(authPath);
};
